
import { supabase } from "@/integrations/supabase/client";
import { Profile, UserRole } from "@/lib/types/auth";
import { toast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";

export function useUserManagementService() {
  const queryClient = useQueryClient();

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: "Role Updated",
        description: "The user's role has been successfully updated.",
      });
    } catch (error) {
      console.error('Error updating role:', error);
      toast({
        title: "Error",
        description: "Failed to update the user's role. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleActiveStatusChange = async (userId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: isActive })
        .eq('id', userId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: "Status Updated",
        description: `User has been ${isActive ? 'activated' : 'deactivated'} successfully.`,
      });
    } catch (error) {
      console.error('Error updating active status:', error);
      toast({
        title: "Error",
        description: "Failed to update the user's status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: "User Deleted",
        description: "The user has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: "Error",
        description: "Failed to delete the user. Please try again.",
        variant: "destructive",
      });
    }
  };

  return {
    handleRoleChange,
    handleActiveStatusChange,
    handleDeleteUser
  };
}
